import { useState } from "react";
import { useFilePicker } from "use-file-picker";

interface FilePickerProps {
  defaultValue?: {
    plainFiles: File[];
    filesContent: (string | ArrayBuffer | null)[];
  };
  accept?: string | string[];
  multiple?: boolean;
  readAs?: "DataURL" | "Text" | "BinaryString" | "ArrayBuffer";
  onFilesSuccessfullySelected?: ({
    plainFiles,
    filesContent,
  }: {
    plainFiles: File[];
    filesContent: (string | ArrayBuffer | null)[];
  }) => void;
}

const useMultiFilePicker = ({
  defaultValue = {
    plainFiles: [],
    filesContent: [],
  },
  accept = "*",
  multiple = true,
  readAs = "DataURL",
  onFilesSuccessfullySelected,
}: FilePickerProps) => {
  const [selectedFiles, setSelectedFiles] = useState<{
    plainFiles: File[];
    filesContent: (string | ArrayBuffer | null)[];
  }>(defaultValue);

  const { openFilePicker } = useFilePicker({
    accept,
    multiple,
    readAs,
    onFilesSuccessfullySelected: ({ plainFiles, filesContent }) => {
      setSelectedFiles((prevFiles) => {
        const updatedFiles = {
          plainFiles: [...prevFiles.plainFiles, ...plainFiles],
          filesContent: [...prevFiles.filesContent, ...filesContent],
        };

        onFilesSuccessfullySelected?.(updatedFiles);
        return updatedFiles;
      });
    },
  });

  const clear = () => {
    setSelectedFiles(() => {
      onFilesSuccessfullySelected?.({
        plainFiles: [],
        filesContent: [],
      });
      return {
        plainFiles: [],
        filesContent: [],
      };
    });
  };

  const remove = (index: number) => {
    setSelectedFiles((prevFiles) => {
      const updatedFiles = {
        plainFiles: prevFiles.plainFiles.filter((_, i) => i !== index),
        filesContent: prevFiles.filesContent.filter((_, i) => i !== index),
      };
      return updatedFiles;
    });
  };

  return {
    openFilePicker,
    files: selectedFiles,
    filesContent: selectedFiles.filesContent,
    clear,
    remove,
  };
};

export default useMultiFilePicker;
